import React, { useState } from 'react';
import { Alert, Button, Empty, Form, Input, Pagination, Select, Skeleton, Statistic, Tag } from 'antd';
import { Link, Navigate, useLocation, useParams } from 'react-router-dom';
import { ClockCircleOutlined, RightOutlined } from '@ant-design/icons';
import FeaturePage from '@/components/featurepage';
import PullToRefresh from '@/components/pulltorefresh';
import ApplicationActions from '@/components/applicationactions';
import UserIdentity from '@/components/useridentity';
import { fetchApplicationSummary, fetchMyApplication, fetchMyApplications } from '@/apis/posts';
import { applicationColors, applicationLabels } from '@/utils/applications';
import usePrivateList from '@/utils/usePrivateList';
import useMobile from '@/utils/useMobile';
import useViewedNotification from '@/utils/useViewedNotification';
import { compactQueryParams, isResourceId, resourceIdRule } from '@/utils/queryParams';
import './index.scss';

const loadApplications = async ({ application_id, ...params }) => {
  if (application_id) {
    if (!isResourceId(application_id)) throw new Error('申请编号无效');
    return { data: [await fetchMyApplication(application_id)] };
  }
  const [list, summary] = await Promise.all([fetchMyApplications(params), fetchApplicationSummary()]);
  if (!Array.isArray(list.data) || !list.pagination) throw new Error('申请列表响应无效');
  return { ...list, summary };
};

const MyApplications = () => {
  const { applicationId } = useParams();
  const location = useLocation();
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({});
  const mobile = useMobile();
  const { data, loading, error, load, sessionKey, isAuthenticated } = usePrivateList(loadApplications,
    applicationId ? { application_id: applicationId } : { ...filters, page, pageSize: 10 },
    applicationId && !isResourceId(applicationId) ? '申请编号无效' : '申请加载失败，记录可能已不存在');
  useViewedNotification(new URLSearchParams(location.search).get('notification'), Boolean(applicationId && data && !loading && !error));
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <FeaturePage embedded showDesktopBack={false} back={applicationId ? '/personal/applications' : '/personal'} className="myApplications personalWorkspace" title={applicationId ? '申请详情' : '我的申请'} subtitle="查看申请进度，管理本人参与的活动。">
    <PullToRefresh enabled={mobile} disabled={loading} onRefresh={load} successMessage="申请已刷新">
      {!applicationId && <section className="featurePage-panel workspace-toolbar">
        {data?.summary && <div className="workspace-summary"><Statistic title="全部申请" value={data.summary.total} />{Object.entries(applicationLabels).map(([key, label]) => <Statistic key={key} title={label} value={data.summary[key]} />)}</div>}
        <Form layout={mobile ? 'vertical' : 'inline'} onFinish={values => { setFilters(compactQueryParams(values)); setPage(1); }}>
          <Form.Item name="status" label="申请状态"><Select allowClear placeholder="全部状态" options={Object.entries(applicationLabels).map(([value, label]) => ({ value, label }))} /></Form.Item>
          <Form.Item name="post_id" label="帖子编号" rules={[resourceIdRule('帖子编号')]}><Input maxLength={19} placeholder="全部帖子" /></Form.Item>
          <Form.Item><Button type="primary" htmlType="submit" disabled={loading}>筛选</Button></Form.Item>
          {!mobile && <Form.Item><Button disabled={loading} onClick={() => load().catch(() => {})}>刷新</Button></Form.Item>}
        </Form>
      </section>}
      {applicationId && !mobile && <div className="myApplications-refresh"><Button disabled={loading} onClick={() => load().catch(() => {})}>刷新</Button></div>}
      {loading ? <Skeleton active /> : error ? <Alert type="error" showIcon title={error} action={<Button onClick={() => load().catch(() => {})}>重试</Button>} /> : <>
        {!data?.data.length && <Empty description="暂无符合条件的申请" />}
        {data?.data.map(item => <section className="featurePage-panel myApplications-item" key={`${sessionKey}:${item.id}`}>
          <div className="myApplications-item-heading"><Link to={`/posts/${item.post}`}>{item.post_title}</Link><Tag className="myApplications-item-status" color={applicationColors[item.status] || 'default'}>{applicationLabels[item.status] || item.status}</Tag></div>
          <div className="myApplications-item-meta">
            <span className="myApplications-item-publisher"><span>发布者</span><UserIdentity user={item.post_owner} /></span>
            <time dateTime={item.created_at}><ClockCircleOutlined aria-hidden="true" />申请于 {new Date(item.created_at).toLocaleString('zh-CN')}</time>
          </div>
          {item.comment_content && <div className="myApplications-item-message"><span>申请留言</span><p>{item.comment_content}</p></div>}
          {(item.comment_is_public === false || item.post_completed_at) && <div className="myApplications-item-flags">
            {item.comment_is_public === false && <Tag>本人私密申请</Tag>}
            {item.post_completed_at && <Tag color="cyan">活动已完成</Tag>}
          </div>}
          {(item.withdrawn_at || item.completion_confirmed_at) && <div className="myApplications-item-updates">
            {item.withdrawn_at && <p>撤回时间：<time dateTime={item.withdrawn_at}>{new Date(item.withdrawn_at).toLocaleString('zh-CN')}</time></p>}
            {item.completion_confirmed_at && <p>本人已确认完成：<time dateTime={item.completion_confirmed_at}>{new Date(item.completion_confirmed_at).toLocaleString('zh-CN')}</time></p>}
          </div>}
          <div className="myApplications-item-footer">
            <ApplicationActions application={item} onChanged={() => load().catch(() => {})} />
            {!applicationId && <Link className="myApplications-item-details" to={`/personal/applications/${item.id}`}>查看申请详情<RightOutlined aria-hidden="true" /></Link>}
          </div>
        </section>)}
        {data?.pagination?.total > 10 && <Pagination current={page} pageSize={10} total={data.pagination.total} showSizeChanger={false} onChange={setPage} />}
      </>}
    </PullToRefresh>
  </FeaturePage>;
};
export default MyApplications;
